import React, { useEffect, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

// Complete fallback patient data
const FALLBACK_PATIENT = {
  id: 1,
  name: "Rudraprasad Kar",
  diabetesType: "Type 2",
  fullName: "Ahmed Ali Hussain",
  gender: "Male",
  location: "Elsheikh Zayed, Giza",
  birthDate: "12 Dec 1992",
  age: 38,
  occupation: "Accountant",
  profileImage: "https://i.imgur.com/0y8Ftya.png",
  bmi: 22.4,
  weight: 92,
  height: 175,
  bloodPressure: "124/80",
  diagnosis: ["Obesity"],
  barriers: ["Uncontrolled Type 2", "Fear of Insulin", "Fear of medication"],
  timeline: [
    "Dec 2022 - Pre-diabetic (A1c: 10.4)",
    "Jan 2022 - Type 2 (A1c: 10.4)",
    "Jul 2021 - Chronic thyroid disorder",
    "Jul 2021 - Angina Pectoris",
    "Jul 2021 - Stroke"
  ],
  medicalHistory: {
    "Chronic Disease": "IHD, Obesity, Chronic thyroid disorder",
    "Diabetes Emergencies": "Diabetic Ketoacidosis",
    "Surgery": "Liposuction",
    "Family Disease": "Obesity (Father)",
    "Diabetes Complications": "Nephropathy, Neuropathy, Retinopathy, Diabetic foot, Sexual dysfunction"
  }
};

const PatientDashboard = () => {
  const [patientData, setPatientData] = useState(FALLBACK_PATIENT);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        // Attempt to fetch from API
        const response = await axios.get('https://64541528e9ac46cedf2a1b61.mockapi.io/patient/1', {
          timeout: 5000 // 5 second timeout
        });
        
        if (response.data) {
          setPatientData(response.data);
        } else {
          throw new Error("Empty response from server");
        }
      } catch (error) {
        console.warn("Using fallback data due to API error:", error.message);
        setApiError(error.message);
        setUsingFallback(true);
        setPatientData(FALLBACK_PATIENT);
      } finally {
        setLoading(false);
      }
    };

    fetchPatientData();
  }, []);

  // Helper functions with type safety
  const getBMIClass = (bmi) => {
    const num = parseFloat(bmi);
    if (isNaN(num)) return '';
    if (num < 18.5) return 'text-warning';
    if (num >= 25) return 'text-danger';
    return 'text-success';
  };

  const getWeightClass = (weight) => {
    const num = parseFloat(weight);
    return !isNaN(num) && num > 90 ? 'text-danger' : 'text-success';
  };

  const getBPClass = (bp) => {
    if (typeof bp !== 'string' || bp === 'N/A') return '';
    const [systolic] = bp.split('/');
    const num = parseInt(systolic);
    return !isNaN(num) && num > 120 ? 'text-danger' : 'text-success';
  };

  const getBarrierClass = (barrier) => {
    const barrierClasses = {
      'Uncontrolled Type 2': 'bg-danger',
      'Fear of Insulin': 'bg-primary',
      'Fear of medication': 'bg-info text-dark'
    };
    return barrierClasses[barrier] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading patient data...</p>
      </div>
    );
  }

  return (
    <div className="container py-4 patient-dashboard">
      {/* API Error Alert (only shows if using fallback data) */}
      {usingFallback && (
        <div className="alert alert-warning alert-dismissible fade show">
          <strong>Note:</strong> Using demo data because the API is unavailable. Error: {apiError}
          <button type="button" className="btn-close" onClick={() => setUsingFallback(false)}></button>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold">
          {patientData.name} <span className="text-success">[ {patientData.diabetesType} ]</span>
        </h3>
        <input type="search" className="form-control w-25" placeholder="Search..." />
      </div>

      {/* Profile Card */}
      <div className="card p-4 mb-4 shadow-sm">
        <div className="row align-items-center">
          <div className="col-md-2 text-center">
            <img 
              src={patientData.profileImage} 
              alt="Patient" 
              className="rounded-circle img-fluid"
              onError={(e) => {
                e.target.src = 'https://i.imgur.com/0y8Ftya.png';
              }}
            />
          </div>
          <div className="col-md-10">
            <h5 className="mb-1">{patientData.fullName}</h5>
            <p className="mb-1">
              👤 {patientData.gender} | 📍 {patientData.location} | 
              📅 {patientData.birthDate} ({patientData.age} years)
            </p>
            <p className="mb-1">💼 {patientData.occupation}</p>
            <div className="row mt-3">
              <div className="col">BMI: <strong className={getBMIClass(patientData.bmi)}>{patientData.bmi}</strong></div>
              <div className="col">Weight: <strong className={getWeightClass(patientData.weight)}>{patientData.weight} kg</strong></div>
              <div className="col">Height: <strong>{patientData.height} cm</strong></div>
              <div className="col">BP: <strong className={getBPClass(patientData.bloodPressure)}>{patientData.bloodPressure}</strong></div>
            </div>
          </div>
        </div>
      </div>

      {/* Diagnosis & Barriers */}
      <div className="mb-4">
        <div className="d-flex flex-wrap gap-2">
          {patientData.diagnosis?.map((item, index) => (
            <span key={`diagnosis-${index}`} className="badge bg-warning text-dark">{item}</span>
          ))}
          {patientData.barriers?.map((item, index) => (
            <span key={`barrier-${index}`} className={`badge ${getBarrierClass(item)}`}>{item}</span>
          ))}
        </div>
      </div>

      {/* Timeline & History */}
      <div className="row">
        {/* Timeline */}
        <div className="col-md-6 mb-4">
          <h5 className="fw-bold mb-3">Timeline</h5>
          <ul className="list-group">
            {patientData.timeline?.map((item, index) => (
              <li key={`timeline-${index}`} className="list-group-item">{item}</li>
            ))}
          </ul>
        </div>

        {/* Medical History */}
        <div className="col-md-6 mb-4">
          <h5 className="fw-bold mb-3">Medical History</h5>
          <ul className="list-group">
            {Object.entries(patientData.medicalHistory || {}).map(([key, value]) => (
              <li key={key} className="list-group-item">
                <strong>{key}:</strong> {value}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;